import * as DialogPrimitive from '@radix-ui/react-dialog';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

interface AutoRefreshConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  // 'sheet' renders inside the Article sheet (absolute, per the project dialog
  // rules); 'page' is a normal portaled modal for the KB list bulk action.
  placement?: 'sheet' | 'page';
}

export const AutoRefreshConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  placement = 'page',
}: AutoRefreshConfirmDialogProps) => {
  const { t } = useTranslation(['knowledge_base']);

  const body = (
    <div className="space-y-4">
      <AlertDialogHeader>
        <AlertDialogTitle>
          <RefreshCw className="inline bg-warning-muted rounded-lg text-warning p-2 box-content size-4" />
          {t('knowledge_base:auto_refresh.confirm.title')}
        </AlertDialogTitle>
      </AlertDialogHeader>

      <AlertDialogDescription>
        {t('knowledge_base:auto_refresh.confirm.body')}
      </AlertDialogDescription>

      <Separator className="my-7" />

      <AlertDialogFooter>
        <AlertDialogCancel asChild>
          <Button size="md" variant="secondary">
            {t('knowledge_base:auto_refresh.confirm.cancel')}
          </Button>
        </AlertDialogCancel>
        <Button size="md" variant="dark" onClick={onConfirm}>
          {t('knowledge_base:auto_refresh.confirm.confirm')}
        </Button>
      </AlertDialogFooter>
    </div>
  );

  if (placement === 'sheet') {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
        <DialogPrimitive.Content
          className="absolute inset-0 flex items-center justify-center p-0 z-30 focus:outline-none"
          role="alertdialog"
          aria-modal="true"
          onEscapeKeyDown={(e) => e.stopPropagation()}
        >
          <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg">{body}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Root>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">{body}</AlertDialogContent>
    </AlertDialog>
  );
};
